import { useState, useCallback } from 'react';
import axios from 'axios';
import { CANVAS_API } from '../config';

/**
 * useEvaluationFlow — shared dialogue evaluation lifecycle.
 *
 * Used by Design Guru dialogues (and re-usable by Direction's
 * ExploreEditor in future) to wrap the /api/canvas/evaluate-section
 * call + modal stage management.
 *
 * Usage:
 *   const evalFlow = useEvaluationFlow({
 *     sectionKey: 'marketing_brand',
 *     sectionTitle: 'Marketing & Brand',
 *     sectionFields: SECTION_FIELDS,
 *     repeatable: false,
 *   });
 *
 *   // At end of dialogue extract:
 *   evalFlow.runEvaluation({
 *     fields: extractedFields,
 *     transcript: { messages, completedAt },
 *     guidance: 'Section guidance text',
 *   }).then((evaluation) => { ... }).catch(...);
 *
 *   // In render:
 *   {evalFlow.evalState && (
 *     <EvaluationModal
 *       section={{ title, icon, colour }}
 *       evalState={evalFlow.evalState}
 *       onContinue={() => { commitSave(); evalFlow.clear(); }}
 *       onRefine={() => { backToChat(); evalFlow.clear(); }}
 *       onCancel={() => evalFlow.clear()}
 *     />
 *   )}
 *
 * State shape (evalState):
 *   null — modal not shown
 *   { stage: 'evaluating', payload, transcript }
 *   { stage: 'done', payload, transcript, evaluation } — modal renders score
 *   { stage: 'error', payload, transcript } — modal renders error
 */
export default function useEvaluationFlow({ sectionKey, sectionTitle, sectionFields, repeatable, scope }) {
  const [evalState, setEvalState] = useState(null);

  const runEvaluation = useCallback(async ({ fields, transcript, guidance }) => {
    if (!fields) return null;
    setEvalState({ stage: 'evaluating', payload: fields, transcript });
    try {
      const fieldsSchema = (sectionFields || []).map(f => ({
        key: f.key,
        label: f.label,
        type: f.type,
        required: !!f.required,
        options: Array.isArray(f.options) ? f.options : undefined,
      }));
      const res = await axios.post(`${CANVAS_API}/evaluate-section`, {
        sectionKey,
        sectionTitle,
        sectionData: fields,
        sectionFields: fieldsSchema,
        repeatable: !!repeatable,
        transcript: transcript || null,
        guidance: guidance || '',
        scope: scope || null,
      });
      const evaluation = res.data?.evaluation;
      if (evaluation) {
        const stamped = { ...evaluation, evaluatedAt: new Date().toISOString() };
        setEvalState({ stage: 'done', payload: fields, transcript, evaluation: stamped });
        return stamped;
      }
      setEvalState({ stage: 'error', payload: fields, transcript });
      return null;
    } catch (err) {
      console.warn('Evaluation failed:', err.message);
      setEvalState({ stage: 'error', payload: fields, transcript });
      return null;
    }
  }, [sectionKey, sectionTitle, sectionFields, repeatable, scope]);

  const clear = useCallback(() => setEvalState(null), []);

  return { evalState, runEvaluation, clear, setEvalState };
}
